import {
  Container,
  Grid,
  Group,
  createStyles,
  useElementSize,
  useToggle,
  useWindowScroll,
} from '@ingeniorforeningen/figurine-core';
import { useParams } from 'react-router-dom';
import { PageHead } from '../components/CaseDetails/PageHead';
import { Message, Messages } from '../components/CaseDetails/Messages';
import { Details } from '../components/CaseDetails/Details';
import { useEffect } from 'react';
import useAppStore from '../store/appStore';
import { ErrorPage } from './ErrorPage';
import { MobileHeader } from '../components/CaseDetails/MobileHeader';
import { stripText } from '../helpers/textsHelpers';
import ScrollToTop from '../components/Common/ScrollToTop';
import { useIsColumn } from '../hooks';
import '../components/CaseDetails/PrintStyling.css';

const HEADER_HEIGHT = 105;
type StyleProps = {
  scrollY: number;
  pageHeadHeight: number;
  showDraw: boolean;
};
const styles = createStyles((theme, { scrollY, pageHeadHeight, showDraw }: StyleProps) => ({
  wrapper: {
    [theme.fn.largerThan('sm')]: {
      position: 'sticky',
      top: 0,
      height: `calc(100vh - ${
        HEADER_HEIGHT +
        (scrollY >= HEADER_HEIGHT + pageHeadHeight ? -pageHeadHeight + 40 : pageHeadHeight + 40)
      }px)`,
      transition: 'height 300ms',
    },
  },
  DetailsDraw: {
    [theme.fn.smallerThan('sm')]: {
      position: 'fixed',
      top: 0,
      bottom: 0,
      right: 0,
      transform: `translateX(${!showDraw ? '100%' : 0})`,
      transition: 'transform 200ms ease-out',
      zIndex: 99999,
    },
  },
}));

const CaseDetails = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const [scroll] = useWindowScroll();
  const { ref: pageHeadRef, height: pageHeadHeight } = useElementSize();
  const [showDraw, toggleDraw] = useToggle([false, true]);
  const { classes } = styles({ scrollY: scroll.y, pageHeadHeight, showDraw });
  const language = useAppStore((state) => state.appLanguage);
  const username = localStorage.getItem('username') || undefined;

  // Methods from store
  const fetchCaseMessages = useAppStore((state) => state.fetchCaseMessages);
  const fetchCaseDetails = useAppStore((state) => state.fetchCaseDetails);
  const fetchCaseDocuments = useAppStore((state) => state.fetchCaseDocuments);
  const postCaseMessage = useAppStore((state) => state.postCaseMessage);
  const updateCaseDetails = useAppStore((state) => state.updateCaseDetails);

  // Loading states
  const loadingTexts = useAppStore((state) => state.appTexts.loading);
  const loadingMessages = useAppStore((state) => state.caseMessages.loading);
  const loadingDocuments = useAppStore((state) => state.caseDocuments.loading);
  const loadingDetails = useAppStore((state) => state.caseDetails.loading);
  const isPostingMessage = useAppStore((state) => state.postingCaseMessage);
  const isDetailsPopulated = useAppStore((state) => state.caseDetails.isCaseDetailsPopulated);

  // Data
  const texts = useAppStore((state) => state.appTexts.data);
  const { caseDetailsTexts: detailsTexts } = texts;
  const messages = useAppStore((state) => state.caseMessages.data);
  const documents = useAppStore((state) => state.caseDocuments.data);
  const details = useAppStore((state) => state.caseDetails.data);

  // Error states
  const errorTexts = useAppStore((state) => state.appTexts.error);
  const errorMessages = useAppStore((state) => state.caseMessages.error);
  const errorDocuments = useAppStore((state) => state.caseDocuments.error);
  const errorDetails = useAppStore((state) => state.caseDetails.error);

  const { isColumn } = useIsColumn();
  useEffect(() => {
    if (!caseId) {
      return;
    }
    fetchCaseMessages(caseId);
    fetchCaseDetails(caseId);
    fetchCaseDocuments(caseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  useEffect(() => {
    if (!loadingTexts && texts && !loadingDetails && details && !isDetailsPopulated) {
      updateCaseDetails(texts, details);
    }
  }, [details, texts, loadingTexts, loadingDetails, updateCaseDetails, isDetailsPopulated]);

  // Error handling
  const error = errorTexts || errorMessages || errorDetails || errorDocuments;
  if (error) {
    return <ErrorPage />;
  }

  return (
    <>
      <ScrollToTop />
      <Container px={isColumn ? 0 : 'md'} id="case-details">
        <Group direction="column" noWrap grow my="xl">
          <div ref={pageHeadRef}>
            <Group px={!isColumn ? 0 : 'md'}>
              <PageHead
                loading={loadingDetails || loadingTexts || !isDetailsPopulated}
                categoryName={details?.categoryName}
                caseNumber={details?.caseNumber}
                description={detailsTexts?.headlines.default.description}
                myCases={detailsTexts?.headlines.default.myCases}
                language={language}
              />
            </Group>
          </div>
          {isColumn && (
            <MobileHeader
              title={stripText(detailsTexts?.detailsAndFiles.header)}
              buttonClick={toggleDraw}
              isScrolledToTop={scroll.y >= HEADER_HEIGHT + pageHeadHeight}
            />
          )}

          <Grid container m={0} columns={3} id="case-details-content">
            <Grid item xs={3} sm={2} py={0} className={classes.wrapper}>
              <Messages
                loading={loadingDetails || loadingMessages || loadingTexts}
                language={language}
                caseDetails={details}
                caseMessages={messages}
                handlePostMessage={(message: Message) =>
                  postCaseMessage(caseId, message.message, message.documents)
                }
                messagesTexts={detailsTexts?.messages}
                errorTexts={detailsTexts?.errors}
                username={username}
                postingCaseState={isPostingMessage}
              />
            </Grid>
            <Grid item xs={3} sm={1} p={0} className={classes.DetailsDraw} id="case-details-draw">
              <Group direction="column">
                <Details
                  closeDraw={toggleDraw}
                  loading={loadingDetails || loadingTexts || loadingDocuments}
                  language={language}
                  detailsAndFilesTexts={detailsTexts?.detailsAndFiles}
                  caseDetails={details}
                  caseDocuments={documents}
                />
              </Group>
            </Grid>
          </Grid>
        </Group>
      </Container>
    </>
  );
};

export default CaseDetails;
